import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuUserCheck, LuDoorOpen, LuRotateCw,
} from 'react-icons/lu';

const REFRESH_MS = 20000;

function timeAgo(iso) {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  if (mins < 1) return 'agora mesmo';
  if (mins === 1) return 'há 1 minuto';
  return `há ${mins} minutos`;
}

const typeBadge = (type) => {
  const map = {
    admin:    { bg: 'var(--purple-soft)', color: 'var(--danger)', label: 'Admin' },
    gestor:   { bg: 'var(--info-soft)', color: 'var(--info)', label: 'Gestor' },
    operador: { bg: 'var(--surface-3)', color: 'var(--text-2)', label: 'Operador' },
  };
  const s = map[type] || map.operador;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

export default function ActiveSessionsPage({ user }) {
  const [sessions, setSessions] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [busyId,   setBusyId]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/auth/sessions')
      .then((r) => { setSessions(r.data); setError(''); })
      .catch((e) => setError(e.response?.data?.error || 'Erro ao carregar sessões ativas'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Atualiza sozinha a cada 20s — sem polling, a lista nunca mostraria
    // sessões que caíram por inatividade (10min) até alguém clicar em algo.
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const handleForceLogout = async (session) => {
    if (!window.confirm(`Encerrar a sessão de "${session.full_name || session.username}"? A pessoa será desconectada imediatamente.`)) return;
    setBusyId(session.id);
    try {
      await api.post(`/auth/sessions/${session.id}/logout`);
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao encerrar sessão');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Sessões Ativas</div>
          <div className="page-card-subtitle">Usuários conectados agora — encerre uma sessão para liberar o login em outro local</div>
        </div>
        <button className="btn-salvar" onClick={load} disabled={loading}>
          <LuRotateCw style={{ marginRight: 6 }} /> Atualizar
        </button>
      </div>

      {error && <div className="alert-custom danger" style={{ marginBottom: 12 }}>{error}</div>}

      {loading && sessions.length === 0 && (
        <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '16px 0' }}>Carregando…</div>
      )}

      {!loading && sessions.length === 0 && (
        <div className="empty-state">
          <LuUserCheck size={32} color="var(--text-3)" style={{ marginBottom: 8 }} />
          <p>Nenhuma sessão ativa no momento</p>
        </div>
      )}

      {sessions.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Usuário', 'Login', 'Tipo', 'Empresa', 'Última atividade', ''].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const isSelf = s.id === user?.id;
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 12px', fontWeight: 700, color: 'var(--text-1)' }}>
                      {s.full_name || s.username}
                      {isSelf && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>(você)</span>}
                    </td>
                    <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>{s.username}</td>
                    <td style={{ padding: '11px 12px' }}>{typeBadge(s.type_user)}</td>
                    <td style={{ padding: '11px 12px' }}>
                      {s.company
                        ? <span style={{ color: 'var(--text-1)' }}>{s.company.name}</span>
                        : <span style={{ color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>Não vinculada</span>}
                    </td>
                    <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>{timeAgo(s.last_activity_at)}</td>
                    <td style={{ padding: '11px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => handleForceLogout(s)}
                        disabled={isSelf || busyId === s.id}
                        title={isSelf ? 'Use "Sair" no menu para encerrar a própria sessão' : 'Forçar logout'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: 'none', border: '1px solid var(--danger-border)', borderRadius: 7,
                          color: isSelf ? 'var(--text-3)' : 'var(--danger)',
                          padding: '6px 12px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                          cursor: isSelf ? 'not-allowed' : 'pointer',
                          opacity: busyId === s.id ? 0.6 : 1,
                        }}
                      >
                        <LuDoorOpen size={12} /> {busyId === s.id ? 'Encerrando…' : 'Forçar logout'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
